import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import i18n, { LANGUAGE_STORAGE_KEY } from './index';
import { DEFAULT_LANGUAGE, detectBrowserLanguage, isLanguageCode, LANGUAGES, Language, LanguageCode } from './languages';
import { useAppSelector } from '@/store';
import { settingsApi } from '@/services/settingsApi';

interface LanguageContextValue {
  /** Active UI locale. */
  language: LanguageCode;
  /** All selectable languages (native labels). */
  languages: readonly Language[];
  /** Switch locale. Persists to localStorage + syncs i18n; PATCHes account when logged in. */
  setLanguage(code: LanguageCode): void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function readStoredLanguage(): LanguageCode {
  if (typeof window === 'undefined') return detectBrowserLanguage();
  try {
    const raw = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isLanguageCode(raw)) return raw;
  } catch {
    /* storage may be unavailable */
  }
  return detectBrowserLanguage() ?? DEFAULT_LANGUAGE;
}

interface Props {
  children: ReactNode;
  /** Optional initial locale override — useful for tests. */
  initialLanguage?: LanguageCode;
  /**
   * Disable the one-shot fetch of the server-saved language + the best-effort
   * PATCH on change. Mirrors ThemeProvider — tests that seed an authenticated
   * auth slice pass this so an unmocked settingsApi call doesn't leak.
   */
  disableAccountSync?: boolean;
}

export function LanguageProvider({ children, initialLanguage, disableAccountSync = false }: Props): JSX.Element {
  const [language, setLanguageState] = useState<LanguageCode>(() => initialLanguage ?? readStoredLanguage());

  const authStatus = useAppSelector((s) => s.auth.status);
  const isGuest = useAppSelector((s) => Boolean(s.auth.user?.guest));
  const isLoggedIn = authStatus === 'authenticated' && !isGuest;
  const loadedFromServerRef = useRef(false);

  // Keep i18next in lockstep with our state (state is the source of truth).
  useEffect(() => {
    void i18n.changeLanguage(language);
  }, [language]);

  // Server value wins once per sign-in (mirrors ThemeContext).
  useEffect(() => {
    if (disableAccountSync) return;
    if (!isLoggedIn) {
      loadedFromServerRef.current = false;
      return;
    }
    if (loadedFromServerRef.current) return;
    loadedFromServerRef.current = true;
    void settingsApi.get()
      .then((res) => {
        const serverLang = res.settings.language;
        if (isLanguageCode(serverLang)) {
          setLanguageState(serverLang);
          try { window.localStorage.setItem(LANGUAGE_STORAGE_KEY, serverLang); } catch { /* storage may be unavailable */ }
        }
      })
      .catch(() => { /* network/auth flakes are non-fatal */ });
  }, [isLoggedIn, disableAccountSync]);

  const setLanguage = useCallback((code: LanguageCode) => {
    setLanguageState(code);
    try { window.localStorage.setItem(LANGUAGE_STORAGE_KEY, code); } catch { /* storage may be unavailable */ }
    if (isLoggedIn && !disableAccountSync) {
      void settingsApi.update({ language: code }).catch(() => {});
    }
  }, [isLoggedIn, disableAccountSync]);

  const value = useMemo<LanguageContextValue>(
    () => ({ language, languages: LANGUAGES, setLanguage }),
    [language, setLanguage],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage() must be used inside a <LanguageProvider>');
  }
  return ctx;
}
